import { type LevelProgressCoachComment, type Level } from '@/lib/types';
import { useCallback, useContext, useState } from 'react';
import RubricsPortfolioContext from '@/lib/contexts/RubricsPortfolioContext';
import TextEditor from '@/components/ChallengePortfolio/NormalView/portfolio-item/PortfolioItemFields/FieldRenderer/TypeSpecificFieldRenderers/utils/TextEditor';
import useApi from '@/lib/api/useApi';
import TonalButton from '@/components/TonalButton';
import { AddCommentOutlined as AddIcon } from '@mui/icons-material';
import PortfolioUserContext from '@/lib/contexts/PortfolioUserContext';
import useRubricsPortfolioApiEndpoint from '../../../useRubricsPortfolioApiEndpoint';
import usePartProgressValues from '../LevelProgressNotes/AddLevelProgressNote/usePartProgressValues';
import { FormattedMessage } from 'react-intl';

type AddLevelProgressCoachCommentProps = {
  level: Level;
};

const AddLevelProgressCoachComment = ({
  level,
}: AddLevelProgressCoachCommentProps) => {
  const api = useApi();
  const rubricsPortfolioApiEndpoint = useRubricsPortfolioApiEndpoint();

  const portfolioUser = useContext(PortfolioUserContext)!;
  const [, setPortfolio] = useContext(RubricsPortfolioContext);
  const [partStudentProgress, partCoachProgress] = usePartProgressValues(level);

  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveNewCoachComment = useCallback(
    async (value: string | null): Promise<void> => {
      if (value === null) {
        setFormOpen(false);

        return;
      }

      setSaving(true);

      const levelProgressCoachComment =
        await api.post<LevelProgressCoachComment>(
          `${rubricsPortfolioApiEndpoint}/level-progress-coach-comments/${level.id}?userEmail=${portfolioUser.email}`,
          {
            content: value,
            studentProgress: partStudentProgress,
            coachProgress: partCoachProgress,
          },
        );

      setPortfolio(portfolio => {
        if (
          typeof portfolio.levelProgressCoachComments![level.id] === 'undefined'
        ) {
          portfolio.levelProgressCoachComments![level.id] = {};
        }

        portfolio.levelProgressCoachComments![level.id][
          levelProgressCoachComment.id
        ] = levelProgressCoachComment;
      });

      setSaving(false);
      setFormOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [level.id],
  );

  if (formOpen) {
    return <TextEditor onSave={saveNewCoachComment} disabled={saving} />;
  }

  return (
    <TonalButton onClick={() => setFormOpen(true)} startIcon={<AddIcon />}>
      <FormattedMessage id="portfolio.level.attachments.level_progress_coach_comments.add.button" />
    </TonalButton>
  );
};

export default AddLevelProgressCoachComment;
